import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { Col, Input, Label, Row } from "reactstrap";
import AppModal from "../../../../Components/Common/AppModal";
import {
  companyStorageLocationList,
  companyStorageLocationCreate,
  companyStorageLocationCreateSuccess,
  companyStorageLocationUpdate,
  companyStorageLocationUpdateSuccess,
  companyStorageLocationDelete,
  companyStorageLocationDeleteSuccess,
  companyStorageLocationData,
} from "../../../../store/companyStorageLocation/action";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";

const EMPTY_FORM = {
  Name: "",
  Description: "",
  CssColourBackground: "#ffffff",
  CssColourBorder: "#cccccc",
  CssColourText: "#000000",
  Order: "",
  FlagActive: "1",
};

export default function TabStorageLocations() {
  const dispatch = useDispatch();

  const {
    listData,
    listLoading,
    locationData,
    createResponse,
    createLoading,
    updateResponse,
    updateLoading,
    deleteResponse,
    deleteLoading,
  } = useSelector((state) => ({
    listData: state.CompanyStorageLocation.companyStorageLocationList?.companystoragelocation_list,
    listLoading: state.CompanyStorageLocation.companyStorageLocationListLoading,
    locationData: state.CompanyStorageLocation.companyStorageLocationData?.companystoragelocation_data,
    createResponse: state.CompanyStorageLocation.companyStorageLocationCreate,
    createLoading: state.CompanyStorageLocation.companyStorageLocationCreateLoading,
    updateResponse: state.CompanyStorageLocation.companyStorageLocationUpdate,
    updateLoading: state.CompanyStorageLocation.companyStorageLocationUpdateLoading,
    deleteResponse: state.CompanyStorageLocation.companyStorageLocationDelete,
    deleteLoading: state.CompanyStorageLocation.companyStorageLocationDeleteLoading,
  }));

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(companyStorageLocationList());
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (!isEdit || isEmpty(locationData)) return;
    setForm({
      Id: locationData.Id,
      Name: locationData.Name || "",
      Description: locationData.Description || "",
      CssColourBackground: locationData.CssColourBackground || "#ffffff",
      CssColourBorder: locationData.CssColourBorder || "#cccccc",
      CssColourText: locationData.CssColourText || "#000000",
      Order: locationData.Order || "",
      FlagActive: locationData.FlagActive ?? "1",
    });
    setModal(true);
  }, [locationData, isEdit]);

  // Create response
  useEffect(() => {
    if (isEmpty(createResponse)) return;
    if (createResponse?.status === 200) {
      toast.success(createResponse.message);
      dispatch(companyStorageLocationList());
      setModal(false);
      setForm(EMPTY_FORM);
    } else {
      toast.error(createResponse.message);
    }
    dispatch(companyStorageLocationCreateSuccess([]));
  }, [createResponse]);

  // Update response
  useEffect(() => {
    if (isEmpty(updateResponse)) return;
    if (updateResponse?.status === 200) {
      toast.success(updateResponse.message);
      dispatch(companyStorageLocationList());
      setModal(false);
      setForm(EMPTY_FORM);
    } else {
      toast.error(updateResponse.message);
    }
    dispatch(companyStorageLocationUpdateSuccess([]));
  }, [updateResponse]);

  // Delete response
  useEffect(() => {
    if (isEmpty(deleteResponse)) return;
    if (deleteResponse?.status === 200) {
      toast.success(deleteResponse.message);
      dispatch(companyStorageLocationList());
      setDeleteModal(false);
      setDeleteId(null);
    } else {
      toast.error(deleteResponse.message);
    }
    dispatch(companyStorageLocationDeleteSuccess([]));
  }, [deleteResponse]);

  const openCreate = () => {
    setIsEdit(false);
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const openEdit = (id) => {
    setIsEdit(true);
    dispatch(companyStorageLocationData({ id }));
  };

  const handleSubmit = () => {
    if (!form.Name?.trim()) { toast.error("Name is required"); return; }
    const payload = [{
      ...(isEdit && { Id: form.Id }),
      Name: form.Name,
      Description: form.Description,
      CssColourBackground: form.CssColourBackground,
      CssColourBorder: form.CssColourBorder,
      CssColourText: form.CssColourText,
      Order: form.Order !== "" ? Number(form.Order) : null,
      FlagActive: form.FlagActive,
    }];
    isEdit ? dispatch(companyStorageLocationUpdate(payload)) : dispatch(companyStorageLocationCreate(payload));
  };

  const f = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const isSaving = createLoading || updateLoading;

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => dispatch(companyStorageLocationDelete([deleteId]))}
        onCloseClick={() => { setDeleteModal(false); setDeleteId(null); }}
      />

      <AppModal
        open={modal}
        onClose={() => setModal(false)}
        title={isEdit ? "Edit Storage Location" : "Add Storage Location"}
        headerAccent="indigo"
        size="sm"
        loading={isSaving}
        onSubmit={handleSubmit}
        submitLabel={isEdit ? "Update" : "Create"}
        submitColor="primary"
      >
        <Row className="g-3">
            <Col lg={12}>
              <Label>Name *</Label>
              <Input value={form.Name} onChange={f("Name")} placeholder="e.g. Office, Paul's Van" />
            </Col>
            <Col lg={12}>
              <Label>Description</Label>
              <Input type="textarea" rows={3} value={form.Description} onChange={f("Description")} />
            </Col>
            <Col lg={4}>
              <Label>Background Colour</Label>
              <Input type="color" value={form.CssColourBackground} onChange={f("CssColourBackground")} />
            </Col>
            <Col lg={4}>
              <Label>Border Colour</Label>
              <Input type="color" value={form.CssColourBorder} onChange={f("CssColourBorder")} />
            </Col>
            <Col lg={4}>
              <Label>Text Colour</Label>
              <Input type="color" value={form.CssColourText} onChange={f("CssColourText")} />
            </Col>
            <Col lg={6}>
              <Label>Order</Label>
              <Input type="number" value={form.Order} onChange={f("Order")} placeholder="0" min={0} />
            </Col>
            <Col lg={6}>
              <Label>Active</Label>
              <div className="form-check form-switch mt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.FlagActive === "1"}
                  onChange={(e) => setForm((p) => ({ ...p, FlagActive: e.target.checked ? "1" : "0" }))}
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="p-2 rounded border d-flex align-items-center gap-2">
                <span className="badge px-3 py-2" style={{
                  background: form.CssColourBackground,
                  border: `1px solid ${form.CssColourBorder}`,
                  color: form.CssColourText,
                }}>
                  {form.Name || "Preview"}
                </span>
                <span className="text-muted small">Colour preview</span>
              </div>
            </Col>
          </Row>
      </AppModal>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Storage Locations</h5>
        <button className="btn btn-success btn-sm" onClick={openCreate}>
          <i className="ri-add-line align-bottom me-1"></i>Add Location
        </button>
      </div>

      {listLoading ? (
        <LoadingSpinner />
      ) : isEmpty(listData) ? (
        <div className="text-center text-muted py-4">
          <i className="ri-map-pin-line fs-1 d-block mb-2"></i>
          No storage locations yet. Click <strong>Add Location</strong> to create one.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Description</th>
                <th>Colours</th>
                <th>Order</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...(listData || [])].sort((a, b) => Number(a.Id) - Number(b.Id)).map((loc) => (
                <tr key={loc.Id}>
                  <td className="text-muted">#{loc.Id}</td>
                  <td>
                    <span className="badge px-2 py-1" style={{
                      background: loc.CssColourBackground || "#f1f5f9",
                      border: `1px solid ${loc.CssColourBorder || "#e2e8f0"}`,
                      color: loc.CssColourText || "#0f172a",
                    }}>
                      {loc.Name}
                    </span>
                  </td>
                  <td className="text-muted small" style={{ maxWidth: 220 }}>
                    <span className="text-truncate d-block">{loc.Description || "—"}</span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <span title="Background" style={{ width: 18, height: 18, borderRadius: 4, background: loc.CssColourBackground || "#f1f5f9", border: "1px solid #e2e8f0", display: "inline-block" }} />
                      <span title="Border" style={{ width: 18, height: 18, borderRadius: 4, background: loc.CssColourBorder || "#e2e8f0", border: "1px solid #e2e8f0", display: "inline-block" }} />
                      <span title="Text" style={{ width: 18, height: 18, borderRadius: 4, background: loc.CssColourText || "#0f172a", border: "1px solid #e2e8f0", display: "inline-block" }} />
                    </div>
                  </td>
                  <td>{loc.Order ?? "—"}</td>
                  <td>
                    <span className={`badge ${loc.FlagActive === "1" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                      {loc.FlagActive === "1" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-soft-info btn-sm btn-icon" title="Edit" onClick={() => openEdit(loc.Id)}>
                        <i className="ri-pencil-fill"></i>
                      </button>
                      <button
                        className="btn btn-soft-danger btn-sm btn-icon"
                        title="Delete"
                        onClick={() => { setDeleteId(loc.Id); setDeleteModal(true); }}
                        disabled={deleteLoading}
                      >
                        <i className="ri-delete-bin-fill"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
